import { ArrowLeft } from 'lucide-react';
import { useEffect, type ReactNode } from 'react';
import { Logo } from '@/components/logo';
import { Button } from '@/components/ui/button';

/**
 * Privacy policy and terms (https://deskworks.ai/privacy, /terms-and-conditions): public on every host, also
 * linked from Google's consent screen of Deskworks's OAuth client. Change `UPDATED` with every change of the text.
 */
const UPDATED = '25 October 2026';

const COMPANY = (
  <address className="not-italic">
    SELLRZ GmbH
    <br />
    Julius-Vosseler-Straße 112 G
    <br />
    22527 Hamburg, Germany
    <br />
    Email: <Mail />
  </address>
);

function Mail() {
  return (
    <a href="mailto:hello@sellrz.io" className="text-foreground underline underline-offset-2">
      hello@sellrz.io
    </a>
  );
}

function A({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noreferrer" className="text-foreground underline underline-offset-2">
      {children}
    </a>
  );
}

function H2({ children }: { children: ReactNode }) {
  return <h2 className="mt-10 text-xl font-semibold tracking-tight text-foreground">{children}</h2>;
}

function H3({ children }: { children: ReactNode }) {
  return <h3 className="mt-6 font-semibold text-foreground">{children}</h3>;
}

function Ul({ children }: { children: ReactNode }) {
  return <ul className="grid list-disc gap-1.5 pl-5">{children}</ul>;
}

function LegalLayout({ title, children }: { title: string; children: ReactNode }) {
  useEffect(() => {
    document.title = `${title} · Deskworks`;
    return () => {
      document.title = 'Deskworks';
    };
  }, [title]);
  return (
    <div className="min-h-full bg-background text-foreground">
      <header className="border-b">
        <div className="mx-auto flex h-16 max-w-3xl items-center gap-4 px-4 sm:px-6">
          <a href="/" className="flex items-center gap-2 font-semibold tracking-tight">
            <Logo className="size-7" /> Deskworks
          </a>
          <Button asChild variant="ghost" size="sm" className="ml-auto">
            <a href="/">
              <ArrowLeft /> Home
            </a>
          </Button>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-4 py-12 sm:px-6 sm:py-16">
        <h1 className="text-3xl font-semibold tracking-tight text-balance sm:text-4xl">{title}</h1>
        <p className="mt-2 text-sm text-muted-foreground">Last updated: {UPDATED}</p>
        <div className="mt-8 grid gap-4 text-[15px] leading-relaxed text-muted-foreground">{children}</div>
      </main>
      <footer className="border-t px-4 py-8 sm:px-6">
        <nav className="mx-auto flex max-w-3xl flex-wrap gap-x-5 gap-y-2 text-sm text-muted-foreground">
          <a href="/privacy" className="hover:text-foreground">
            Privacy
          </a>
          <a href="/terms-and-conditions" className="hover:text-foreground">
            Terms
          </a>
          <a href="https://sellrz.io/imprint" className="hover:text-foreground">
            Imprint
          </a>
          <span className="ml-auto text-xs">© {new Date().getFullYear()} SELLRZ GmbH</span>
        </nav>
      </footer>
    </div>
  );
}

export function PrivacyPage() {
  return (
    <LegalLayout title="Privacy Policy">
      <p>
        This policy explains how SELLRZ GmbH processes personal data when you visit deskworks.ai, request access, or use Deskworks at app.deskworks.ai
        (together “Deskworks”), and how Deskworks handles data from services you connect, including Google user data.
      </p>

      <H2>1. Controller</H2>
      {COMPANY}
      <p>
        Commercial register: Amtsgericht Hamburg, HRB 199377. Managing director: Kay Niklas Fleischmann. For anything about your data, write to <Mail />.
      </p>

      <H2>2. Hosting and server logs</H2>
      <p>
        Deskworks runs on servers of Hetzner Online GmbH, Industriestr. 25, 91710 Gunzenhausen, Germany, in data centers in Helsinki (Finland) and Nuremberg
        (Germany). Files are stored in Hetzner object storage in Nuremberg.
      </p>
      <p>
        When you open Deskworks, the servers log technical data your browser sends: IP address, date and time, the page requested, status code, and browser
        type. We use it to deliver the service and to detect and fix errors and abuse (Art. 6(1)(f) GDPR) and delete it regularly.
      </p>

      <H2>3. Cookies and local storage</H2>
      <p>
        Deskworks uses no tracking, analytics or advertising cookies and no third-party trackers. The browser’s local storage keeps your light or dark mode
        and, once you sign in, your session. Both are strictly necessary for what you asked for (§ 25(2) TDDDG) and stay on your device.
      </p>

      <H2>4. Requesting access</H2>
      <p>
        When you request access on deskworks.ai, we process your name, company, work email, country and, if you give them, phone number and message, to
        answer you and set up your account (Art. 6(1)(b) and (f) GDPR). The request is stored in our customer relationship management system. We delete it
        when it is no longer needed, unless you become a customer or we must keep it by law.
      </p>

      <H2>5. Your account and workspaces</H2>
      <p>
        To use Deskworks we process your name, email address, a hash of your password, your roles, and what you do in Deskworks (for example desks, work
        items, conversations, approvals, files and activity logs). We need this to provide the service under our contract (Art. 6(1)(b) GDPR).
      </p>
      <p>
        Content that you or your organization put into Deskworks, or that agents fetch from connected systems, is processed on behalf of your organization.
        For that content your organization is the controller and we act as its processor (Art. 28 GDPR). Business customers can conclude a data processing
        agreement with us; write to <Mail />.
      </p>
      <p>
        Access tokens, passwords and other credentials you save for a connection are encrypted (AES-256-GCM), used only for the calls the connection makes,
        and never shown again.
      </p>

      <H2>6. AI models</H2>
      <p>
        Deskworks agents work with AI models from providers your workspace sets up, such as Anthropic (Claude) or OpenAI, using your organization’s own API
        key or subscription. To do a task, Deskworks sends the provider the instructions, context and data the task needs. The provider’s terms and privacy
        policy apply; these providers may process data in the United States, under the EU–US Data Privacy Framework or standard contractual clauses.
      </p>

      <H2>7. Connected services</H2>
      <p>
        You decide which systems Deskworks connects to (for example GitLab, Google Search Console, Google Analytics, Meta Ads or DataForSEO) and which desks
        may use them. Deskworks calls these services only as your connections and approvals allow, and their own terms and privacy policies apply.
      </p>

      <H3>Google user data</H3>
      <p>
        When you connect Google Search Console or Google Analytics, you sign in with your Google account and grant Deskworks read-only access:
      </p>
      <Ul>
        <li>
          <strong className="text-foreground">Google Search Console</strong> (scope <code>webmasters.readonly</code>): the properties your account can see,
          search performance (clicks, impressions, click-through rate, position by query, page, country, device and date), URL inspection results, and
          submitted sitemaps.
        </li>
        <li>
          <strong className="text-foreground">Google Analytics</strong> (scope <code>analytics.readonly</code>): the accounts and properties your account can
          see, reports and real-time reports of those properties, and their dimension and metric definitions.
        </li>
      </Ul>
      <p>
        <strong className="text-foreground">How we use it.</strong> Deskworks fetches this data only when an agent in your workspace needs it for a task you
        or your organization gave it, for example a search performance report or an indexing check, and shows the results in your workspace. Deskworks
        cannot change anything in your Google accounts.
      </p>
      <p>
        <strong className="text-foreground">How we store it.</strong> Google’s access and refresh tokens are stored encrypted. The data itself is not copied
        in bulk; it is kept only where an agent puts results into your workspace (for example a report or a work item), for as long as your organization
        keeps them.
      </p>
      <p>
        <strong className="text-foreground">How we share it.</strong> We do not sell Google user data, use it for advertising, or use it to develop or train
        generalized AI or machine learning models. It is passed on only to do the task you asked for: to the AI model provider your workspace uses (section 6)
        and to the people in your workspace. No human at SELLRZ reads it, unless you ask us to for support, it is needed for security or to comply with the
        law.
      </p>
      <p>
        <strong className="text-foreground">How to revoke it.</strong> Choose “Sign out” on the connection page or delete the connection; Deskworks then deletes
        the tokens. You can also remove Deskworks’s access at <A href="https://myaccount.google.com/permissions">myaccount.google.com/permissions</A>. To have
        data from Google deleted from your workspace, delete it there or write to <Mail />.
      </p>
      <p>
        Deskworks’s use and transfer to any other app of information received from Google APIs will adhere to the{' '}
        <A href="https://developers.google.com/terms/api-services-user-data-policy">Google API Services User Data Policy</A>, including the Limited Use
        requirements.
      </p>

      <H2>8. Recipients</H2>
      <p>
        We share personal data only as described here: with Hetzner as our hosting provider, with the AI model providers and connected services your
        workspace uses, and with authorities where the law requires it. We never sell personal data.
      </p>

      <H2>9. Retention</H2>
      <p>
        We keep account and workspace data for as long as your organization uses Deskworks. After a workspace or account is deleted, its data is removed,
        except where we must keep it by law (for example invoices, for up to ten years under German commercial and tax law).
      </p>

      <H2>10. Your rights</H2>
      <p>
        You have the right to access, rectification, erasure, restriction of processing and data portability, the right to object to processing based on
        Art. 6(1)(f) GDPR, and the right to withdraw any consent with effect for the future. Write to <Mail />. You can also complain to a supervisory
        authority, for example the Hamburg Commissioner for Data Protection and Freedom of Information (HmbBfDI).
      </p>

      <H2>11. Security</H2>
      <p>
        Connections to Deskworks are encrypted with TLS. Credentials and sign-in tokens are encrypted at rest, workspaces are separated from each other, and
        agents only reach the systems a desk was given.
      </p>

      <H2>12. Changes</H2>
      <p>We update this policy when Deskworks or the law changes. The date at the top shows the current version.</p>
    </LegalLayout>
  );
}

export function TermsPage() {
  return (
    <LegalLayout title="Terms and Conditions">
      <H2>1. Scope</H2>
      <p>
        These terms govern the use of Deskworks (deskworks.ai and app.deskworks.ai), a service of SELLRZ GmbH, Julius-Vosseler-Straße 112 G, 22527 Hamburg,
        Germany (“we”). They apply to every organization that uses Deskworks (“customer”) and to the people it invites. Deviating terms of the customer do not
        apply unless we agree to them in writing.
      </p>
      <p>
        Deskworks is offered exclusively to businesses within the meaning of § 14 of the German Civil Code (BGB). Use by consumers (§ 13 BGB) is excluded.
      </p>

      <H2>2. The service</H2>
      <p>
        Deskworks lets customers set up desks of AI agents that work on tasks, use connected systems, and ask people for approval before risky actions. What
        a desk may do is set by the customer: its connections, permissions and approval rules.
      </p>
      <p>
        We develop Deskworks continuously and may change, add or remove features, as long as the core purpose of the service is kept. Access is currently
        granted by invitation; features, limits and any fees are those agreed with the customer.
      </p>

      <H2>3. Accounts</H2>
      <p>
        Customers must give accurate information, keep sign-in details confidential, and make sure that only authorized people use their accounts. The
        customer is responsible for what happens in its workspaces and tells us without delay if it suspects misuse.
      </p>

      <H2>4. AI output and agent actions</H2>
      <p>
        Agents produce results with AI models, which can be incomplete or wrong. The customer checks results before relying on them and decides which actions
        agents may take on their own and which need approval. Actions that a person approves, or that the customer has allowed without approval, are the
        customer’s actions.
      </p>

      <H2>5. Connected systems and third-party services</H2>
      <p>
        The customer may only connect systems and accounts it is authorized to use, and is responsible for keeping the credentials it saves valid and
        lawful. AI model providers (for example Anthropic or OpenAI) and connected services (for example Google, GitLab or Meta) are used under their own terms,
        through the customer’s own keys, subscriptions or sign-ins. We are not responsible for their availability or results.
      </p>

      <H2>6. Acceptable use</H2>
      <p>The customer will not use Deskworks to:</p>
      <Ul>
        <li>break the law or infringe the rights of others;</li>
        <li>send spam, malware or content that is unlawful, deceptive or harmful;</li>
        <li>attack, overload or test the security of Deskworks or of third-party systems without permission;</li>
        <li>get around usage limits or the security of other workspaces.</li>
      </Ul>
      <p>We may suspend access temporarily if there are concrete signs of such use; we tell the customer and lift the suspension once the cause is gone.</p>

      <H2>7. Customer data</H2>
      <p>
        Content the customer puts into Deskworks or that agents fetch for it remains the customer’s. We use it only to provide Deskworks, never to train AI
        models, and process personal data in it on the customer’s behalf; on request we conclude a data processing agreement (Art. 28 GDPR). Our{' '}
        <a href="/privacy" className="text-foreground underline underline-offset-2">
          privacy policy
        </a>{' '}
        describes the details.
      </p>

      <H2>8. Rights to Deskworks</H2>
      <p>
        For the term of the contract the customer has a non-exclusive, non-transferable right to use Deskworks for its own business purposes. All rights to the
        software, desks and skills we provide remain with us. Results agents produce for the customer belong to the customer.
      </p>

      <H2>9. Availability</H2>
      <p>
        We aim for high availability but do not guarantee uninterrupted operation. Maintenance, updates and disruptions outside our control (for example at
        hosting, AI or connected service providers) can interrupt the service.
      </p>

      <H2>10. Liability</H2>
      <p>
        We are liable without limitation for intent and gross negligence, for injury to life, body or health, under the Product Liability Act, and where we
        have given a guarantee. For slight negligence we are liable only for breaches of essential contractual obligations (obligations whose fulfilment makes
        the contract possible and on which the customer may rely), limited to the damage typical and foreseeable when the contract was made. Any further
        liability is excluded. The customer is responsible for backing up data that it cannot afford to lose.
      </p>

      <H2>11. Term and termination</H2>
      <p>
        Unless agreed otherwise, the contract runs for an indefinite period and either party can end it with 30 days’ notice to the end of a month. The right
        to terminate for good cause remains. After the end of the contract the customer can export its data for 30 days; then we delete it, unless the law
        requires us to keep it.
      </p>

      <H2>12. Changes to these terms</H2>
      <p>
        We may change these terms for valid reasons, such as new features or changes in the law. We announce changes at least six weeks in advance by email or
        in Deskworks. If the customer does not object before they take effect, the changes apply; we point this out in the announcement.
      </p>

      <H2>13. Final provisions</H2>
      <p>
        German law applies, excluding the UN Convention on Contracts for the International Sale of Goods. The place of jurisdiction is Hamburg. Should a
        provision be invalid, the rest of these terms remain valid.
      </p>
      <p>
        Contact: <Mail />
      </p>
    </LegalLayout>
  );
}
